import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "../components/firebaseConfig";
import "./SignIn.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SignIn() {
    const navigate = useNavigate();
    const location = useLocation();
    const redirectTo = location.state?.from || "/dashboard";


    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const [err, setErr] = useState("");              // sign-in errors
    const [note, setNote] = useState("");            // forgot-password notices
    const [noteType, setNoteType] = useState("info");// "info" | "success" | "error"

    const [submitting, setSubmitting] = useState(false);
    const [sendingReset, setSendingReset] = useState(false);

    const canSubmit = EMAIL_RE.test(email) && password.length >= 6 && !submitting;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setErr("");
        setNote("");

        setSubmitting(true);
        try {
            await signInWithEmailAndPassword(auth, email.trim(), password);
            navigate(redirectTo, { replace: true });
        } catch (e) {
            let msg = "Unable to sign in. Please try again.";
            switch (e.code) {
                case "auth/invalid-email": msg = "That email looks invalid."; break;
                case "auth/user-disabled": msg = "This account has been disabled."; break;
                case "auth/user-not-found": msg = "No account found with that email."; break;
                case "auth/wrong-password":
                case "auth/invalid-credential": msg = "Incorrect email or password."; break;
                case "auth/too-many-requests": msg = "Too many attempts. Please wait and try again."; break;
                default: break;
            }
            setErr(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const onForgot = async () => {
        // Reset note state
        setNote("");
        setNoteType("info");

        // Require a syntactically valid email first (brown text, centered)
        if (!EMAIL_RE.test(email)) {
            setNote("Enter your email above, then tap ‘Forgot password?’");
            setNoteType("info");
            return;
        }

        try {
            setSendingReset(true);
            // With enumeration protection, this resolves even if no user exists.
            await sendPasswordResetEmail(auth, email.trim());
            setNote("If an account exists for this email, you’ll receive a reset link.");
            setNoteType("info"); // green
        } catch (e) {
            // Only show real failures as red
            let msg = "Couldn’t send reset email. Please try again.";
            switch (e.code) {
                case "auth/invalid-email": msg = "That email looks invalid."; break;
                case "auth/too-many-requests": msg = "Too many attempts. Please wait and try again."; break;
                case "auth/network-request-failed": msg = "Network error. Please try again."; break;
                // user-not-found won’t be thrown with enumeration protection
                default: break;
            }
            setNote(msg);
            setNoteType("error"); // red
        } finally {
            setSendingReset(false);
        }
    };


    return (
        <div className="signin-container">
            <h2 className="signin-title">Welcome Back</h2>

            <form className="signin-form" onSubmit={onSubmit}>
                <div className="signin-field">
                    <input
                        type="email"
                        placeholder="Email"
                        className="signin-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        inputMode="email"
                    />
                </div>

                <div className="signin-field signin-password-wrap">
                    <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        className="signin-input"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                    />
                    <button
                        type="button"
                        className="toggle-password"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        onClick={() => setShowPassword((v) => !v)}
                    >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                    </button>
                </div>

                {/* Centered helper row */}
                <div className="signin-aux">
                    <button
                        type="button"
                        className="signin-forgot"
                        onClick={onForgot}
                        disabled={sendingReset}
                    >
                        {sendingReset ? "Sending…" : "Forgot password?"}
                    </button>
                    {note && (
                        <div
                            className={`signin-note ${noteType === "success" ? "is-success" :
                                noteType === "error" ? "is-error" : "is-info"
                                }`}
                        >
                            {note}
                        </div>
                    )}
                </div>

                {err && <div className="signin-error-message">{err}</div>}

                <button type="submit" className="signin-btn" disabled={!canSubmit}>
                    {submitting ? "Signing in…" : "Sign In"}
                </button>
            </form>
        </div>
    );
}
